import { RefObject, useCallback, useEffect, useRef, useState } from "react";

import { isReadonlyArray } from "@tsmono/util";

const asArray = <T>(v: T | ReadonlyArray<T>): ReadonlyArray<T> =>
  isReadonlyArray(v) ? v : [v];

const sameItems = <T>(a: ReadonlyArray<T>, b: ReadonlyArray<T>): boolean =>
  a.length === b.length && a.every((item, i) => item === b[i]);

export interface UseScrollDirectionOptions {
  /** Minimum px delta before recognizing a direction change. Default: 15 */
  threshold?: number;
  /** Lock duration (ms) after a state change, matching the CSS transition.
   *  Prevents the height change from the collapse/expand animation from
   *  immediately triggering the opposite direction. Default: 250 */
  transitionLockMs?: number;
  /** When current value is true, all scroll events are ignored and the
   *  direction anchor is reset when suppression ends. Useful for
   *  programmatic scrolls (e.g. outline click → scrollToEvent). */
  suppressRef?: React.RefObject<boolean>;
  /** When true, `hidden` only flips false→true on downward scroll. Upward
   *  scroll motion no longer reveals; once collapsed, the header stays
   *  collapsed until the scroller actually reaches the top (scrollTop ≤ 0),
   *  at which point the existing top-reveal path still fires. Default: false
   *  (symmetric show/hide on direction changes). */
  stayHiddenOnUpScroll?: boolean;
  /** Start hidden (read once, on mount). For surfaces that mount already
   *  "scrolled down" — e.g. a deep-link landing — so the headroom never
   *  paints expanded on the first frame and then blinks away. Default: false */
  initialHidden?: boolean;
}

export interface UseScrollDirectionResult {
  /** True when scrolling down past threshold while scrollTop > 10px */
  hidden: boolean;
  /** Call before a programmatic scroll or layout change that will shift
   *  scrollTop without the user actually scrolling. Resets the direction
   *  anchor to the current scrollTop and engages the transition lock so
   *  the resulting scroll-position shift is ignored.
   *
   *  @param debounce When true, each scroll event that arrives during the
   *    lock resets the expiry timer so the lock stays active until scrolling
   *    stops (useful for virtual-list multi-pass settling). When false (default),
   *    the lock uses a fixed timeout matching the CSS transition duration. */
  resetAnchor: (debounce?: boolean) => void;
  /** Imperatively set the hidden state. Used by callers that drive scroll
   *  motions for which `hidden` should match an explicit direction (e.g.
   *  search Next = forward = collapse) — calling `resetAnchor` alone would
   *  freeze the prior state instead of advancing to the new intended one. */
  setHidden: (hidden: boolean) => void;
}

/**
 * Tracks scroll direction on one or more container elements with hysteresis
 * to prevent jitter. Returns a `hidden` boolean suitable for driving
 * headroom-style show/hide behaviors.
 *
 * Uses per-element anchor-based thresholds: a given element's direction only
 * changes after the user scrolls at least `threshold` px from that element's
 * last recognized change point. Scroll events on any element participate —
 * useful when the view has several independently-scrollable regions (e.g.
 * a main list plus side panels) that should all drive the same headroom.
 *
 * After each state change, updates are suppressed for `transitionLockMs` to
 * let CSS animations settle without triggering feedback loops.
 *
 * Scroll containers may be conditionally rendered (refs start as null).
 * An internal MutationObserver on `document.body` detects when elements
 * appear so scroll listeners are attached promptly.
 */
export function useScrollDirection(
  scrollRef:
    | RefObject<HTMLElement | null>
    | ReadonlyArray<RefObject<HTMLElement | null>>,
  options?: UseScrollDirectionOptions
): UseScrollDirectionResult {
  const threshold = options?.threshold ?? 40;
  const transitionLockMs = options?.transitionLockMs ?? 250;
  const suppressRef = options?.suppressRef;
  const stayHiddenOnUpScroll = options?.stayHiddenOnUpScroll ?? false;

  // Per-element anchor & last-direction tracking so multiple independent
  // scrollers don't cross-contaminate each other's threshold computations.
  const directionAnchorsRef = useRef<WeakMap<HTMLElement, number>>(
    new WeakMap()
  );
  const lastDirectionsRef = useRef<WeakMap<HTMLElement, "up" | "down">>(
    new WeakMap()
  );
  const transitionLockedRef = useRef(false);
  // True when the lock was set by resetAnchor (programmatic scroll).
  // Programmatic locks debounce: each scroll event resets the timer so the
  // lock stays active while the virtual list settles. Transition locks (from
  // setHidden) use a fixed timeout so direction reversals aren't delayed.
  const programmaticLockRef = useRef(false);
  const lockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [hidden, setHidden] = useState(options?.initialHidden ?? false);

  const releaseLock = useCallback(() => {
    transitionLockedRef.current = false;
    programmaticLockRef.current = false;
    if (lockTimerRef.current !== null) {
      clearTimeout(lockTimerRef.current);
      lockTimerRef.current = null;
    }
  }, []);

  // Engage the lock for one `transitionLockMs` window, replacing any pending
  // expiry AND its programmatic flag — a transition lock that supersedes a
  // resetAnchor lock must not inherit its self-extending debounce semantics.
  const engageLock = useCallback(
    (programmatic: boolean, onExpire?: () => void) => {
      transitionLockedRef.current = true;
      programmaticLockRef.current = programmatic;
      if (lockTimerRef.current !== null) {
        clearTimeout(lockTimerRef.current);
      }
      lockTimerRef.current = setTimeout(() => {
        transitionLockedRef.current = false;
        programmaticLockRef.current = false;
        lockTimerRef.current = null;
        onExpire?.();
      }, transitionLockMs);
    },
    [transitionLockMs]
  );

  // The listener effect and the scroller-change reset below key on this
  // array's identity, so it only changes when the refs themselves do —
  // callers may pass a fresh array (or a different number of refs) each
  // render.
  const incomingRefs = asArray(scrollRef);
  const [refArray, setRefArray] = useState(incomingRefs);
  const primaryRefChanged = refArray[0] !== incomingRefs[0];
  if (!sameItems(refArray, incomingRefs)) setRefArray(incomingRefs);

  // The primary scroll element (used by resetAnchor — typically the main
  // list's scroller).
  const primaryRef = refArray[0];

  // Resolve the actual DOM elements. Refs may be conditionally rendered
  // (null on first mount). A MutationObserver picks them up when they
  // appear.
  const [scrollEls, setScrollEls] = useState<HTMLElement[]>([]);

  useEffect(() => {
    const sync = () => {
      const resolved = refArray
        .map((r) => r.current)
        .filter((el): el is HTMLElement => el != null);
      setScrollEls((prev) => (sameItems(prev, resolved) ? prev : resolved));
    };
    sync();

    const observer = new MutationObserver(sync);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [refArray]);

  // Reset hidden when the primary scroller changes — a different ref from the
  // caller (secondary scrollers joining or leaving don't count), or the same
  // logical scroller remounting its element (loading→loaded
  // swaps, content switches): a fresh scroller starts at the top, where the
  // headroom shows. EXCEPT while suppressed: a nav-owned deep-link mount
  // swaps its element mid-landing, and wiping the forced/initial state there
  // painted the headroom expanded right before the landing re-collapsed it.
  const [prevPrimary, setPrevPrimary] = useState<HTMLElement | null>(
    scrollEls[0] ?? null
  );
  const primaryEl = scrollEls[0] ?? null;
  const scrollerChanged =
    primaryRefChanged || (prevPrimary !== primaryEl && prevPrimary !== null);
  if (prevPrimary !== primaryEl) setPrevPrimary(primaryEl);
  // eslint-disable-next-line react-hooks/refs -- deliberate render-phase gate: the reset must be suppressed in the SAME render the scroller swaps, or the headroom paints expanded for a frame before a nav-owned landing re-collapses it
  if (scrollerChanged && hidden && !suppressRef?.current) {
    setHidden(false);
  }

  // Attach scroll listeners to all resolved elements.
  useEffect(() => {
    directionAnchorsRef.current = new WeakMap();
    lastDirectionsRef.current = new WeakMap();
    // Lock state belongs to the previous scroller set — release it wholesale
    // (a stale programmatic flag surviving the swap would make the next
    // transition lock self-extend).
    releaseLock();

    // Track previous suppression state to reset anchors when suppression ends.
    let wasSuppressed = suppressRef?.current ?? false;

    const makeHandler = (el: HTMLElement) => () => {
      const isSuppressed = suppressRef?.current ?? false;

      // When suppression ends, reset this element's anchor to the current
      // position so the first real scroll after the programmatic one starts
      // fresh.
      if (wasSuppressed && !isSuppressed) {
        directionAnchorsRef.current.set(el, el.scrollTop);
      }
      wasSuppressed = isSuppressed;

      if (isSuppressed) {
        directionAnchorsRef.current.set(el, el.scrollTop);
        return;
      }

      const scrollTop = el.scrollTop;

      // At the very top — always reveal headroom and reset anchor so the
      // threshold doesn't fight subsequent downward scrolls.
      if (scrollTop <= 0) {
        directionAnchorsRef.current.set(el, 0);
        lastDirectionsRef.current.set(el, "up");
        if (!transitionLockedRef.current) {
          setHidden(false);
        }
        return;
      }

      // At the very bottom — ignore overscroll bounce.
      const atBottom = el.scrollHeight - scrollTop - el.clientHeight < 1;
      if (atBottom) {
        directionAnchorsRef.current.set(el, scrollTop);
        return;
      }

      const anchor = directionAnchorsRef.current.get(el) ?? 0;
      const delta = scrollTop - anchor;

      if (Math.abs(delta) < threshold) return;

      const direction = delta > 0 ? "down" : "up";
      const prevDirection = lastDirectionsRef.current.get(el) ?? "down";
      const directionChanged = direction !== prevDirection;

      directionAnchorsRef.current.set(el, scrollTop);

      if (directionChanged) {
        lastDirectionsRef.current.set(el, direction);
      }

      if (transitionLockedRef.current) {
        if (programmaticLockRef.current && lockTimerRef.current !== null) {
          // Debounce: extend the programmatic lock until scrolling idles,
          // then re-anchor at the settled position.
          engageLock(true, () => {
            directionAnchorsRef.current.set(el, el.scrollTop);
          });
        }
        return;
      }

      // Opt-in one-way mode: ignore upward scroll motion entirely so a
      // collapsed header stays collapsed until the scroller hits the top
      // (handled by the scrollTop <= 0 branch above).
      if (stayHiddenOnUpScroll && direction === "up") return;

      const shouldHide = direction === "down" && scrollTop > 10;
      setHidden((prev) => {
        if (prev === shouldHide) return prev;
        engageLock(false);
        return shouldHide;
      });
    };

    const handlers = scrollEls.map((el) => {
      const handler = makeHandler(el);
      el.addEventListener("scroll", handler, { passive: true });
      return { el, handler };
    });
    return () => {
      for (const { el, handler } of handlers) {
        el.removeEventListener("scroll", handler);
      }
      // Covers unmount too — no pending expiry timer outlives the hook.
      releaseLock();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- suppressRef is a stable ref (read at event time, never during render); listing it trips react-hooks/refs
  }, [scrollEls, threshold, stayHiddenOnUpScroll, engageLock, releaseLock]);

  const resetAnchor = useCallback(
    (debounce?: boolean) => {
      const primary = primaryRef?.current ?? null;
      if (primary) {
        directionAnchorsRef.current.set(primary, primary.scrollTop);
      }
      engageLock(!!debounce);
    },
    [primaryRef, engageLock]
  );

  const setHiddenExternal = useCallback(
    (next: boolean) => {
      setHidden((prev) => {
        if (prev === next) return prev;
        // Match the internal state-change path: engage the transition lock so
        // scroll events from the imminent imperative scroll don't fight us.
        engageLock(false);
        return next;
      });
    },
    [engageLock]
  );

  return { hidden, resetAnchor, setHidden: setHiddenExternal };
}
